import { useState, useEffect } from "react";
import axios from "axios";
import { DashboardLayout, workerNavItems } from "@/components/DashboardLayout";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Progress } from "@/components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { 
  FileText, 
  Users, 
  CheckCircle2, 
  Clock, 
  Signature,
  PenLine,
  DollarSign,
  MapPin,
  Calendar,
  AlertCircle,
  ChevronRight,
  Gift
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WorkerAgreements() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [agreements, setAgreements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAgreement, setSelectedAgreement] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [signing, setSigning] = useState(false);
  const [signature, setSignature] = useState("");
  const [cedePercentage, setCedePercentage] = useState(0);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    fetchAgreements();
  }, []);

  const fetchAgreements = async () => {
    try {
      const response = await axios.get(`${API}/team-agreements`);
      // Handle both array response and {agreements: [...]} response
      const data = response.data;
      if (Array.isArray(data)) {
        setAgreements(data);
      } else if (data?.agreements) {
        setAgreements(data.agreements);
      } else {
        setAgreements([]);
      }
    } catch (error) {
      console.error("Error fetching agreements:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateAllocation = async (agreementId) => {
    setUpdating(true);
    try {
      await axios.put(`${API}/team-agreements/${agreementId}/allocations?cede_percentage=${cedePercentage}`);
      toast.success("Allocation updated!");
      fetchAgreements();
      const updated = await axios.get(`${API}/team-agreements/${agreementId}`);
      setSelectedAgreement(updated.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update allocation");
    } finally {
      setUpdating(false);
    }
  };

  const handleApprove = async (agreementId) => {
    try {
      await axios.put(`${API}/team-agreements/${agreementId}/approve`);
      toast.success("Allocation approved!");
      fetchAgreements();
      if (selectedAgreement?.agreement_id === agreementId) {
        const updated = await axios.get(`${API}/team-agreements/${agreementId}`);
        setSelectedAgreement(updated.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to approve");
    }
  };

  const handleSign = async (agreementId) => {
    if (!signature.trim()) {
      toast.error("Please enter your signature");
      return;
    }
    
    setSigning(true);
    try {
      await axios.put(`${API}/team-agreements/${agreementId}/sign?signature=${encodeURIComponent(signature)}`);
      toast.success("Agreement signed!");
      setSignature("");
      fetchAgreements();
      if (selectedAgreement?.agreement_id === agreementId) {
        const updated = await axios.get(`${API}/team-agreements/${agreementId}`);
        setSelectedAgreement(updated.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to sign");
    } finally {
      setSigning(false);
    }
  };

  const openAgreement = async (agreement) => {
    try {
      const response = await axios.get(`${API}/team-agreements/${agreement.agreement_id}`);
      setSelectedAgreement(response.data);
      
      // Set initial cede percentage from current allocation
      const myAllocation = response.data.members?.find(m => m.user_id === user?.user_id);
      if (myAllocation) {
        setCedePercentage(myAllocation.ceded_percentage || 0);
      }
      
      setDialogOpen(true);
    } catch (error) {
      toast.error("Failed to load agreement details");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "draft": return "bg-gray-500/20 text-gray-400";
      case "pending_approval": return "bg-yellow-500/20 text-yellow-400";
      case "all_approved": return "bg-blue-500/20 text-blue-400";
      case "signed": return "bg-green-500/20 text-green-400";
      default: return "bg-gray-500/20 text-gray-400";
    }
  };

  const getMyAllocation = (agreement) => {
    return agreement.members?.find(m => m.user_id === user?.user_id);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('nb-NO', { 
      style: 'currency', 
      currency: 'NOK',
      maximumFractionDigits: 0
    }).format(amount);
  };

  return (
    <DashboardLayout navItems={workerNavItems} title="My Agreements">
      <div className="space-y-6" data-testid="worker-agreements-page">
        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card className="bg-[#0A0A0A] border-white/10">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-[#23F7DD]/10 rounded-lg">
                  <FileText className="w-5 h-5 text-[#23F7DD]" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#23F7DD]">{agreements.length}</p>
                  <p className="text-xs text-[#94A3B8]">Total Agreements</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-[#0A0A0A] border-white/10">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-yellow-500/10 rounded-lg">
                  <Clock className="w-5 h-5 text-yellow-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-yellow-400">
                    {agreements.filter(a => !getMyAllocation(a)?.approved).length}
                  </p>
                  <p className="text-xs text-[#94A3B8]">Needs Your Approval</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-[#0A0A0A] border-white/10">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-500/10 rounded-lg">
                  <CheckCircle2 className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-green-400">
                    {agreements.filter(a => a.status === "signed").length}
                  </p>
                  <p className="text-xs text-[#94A3B8]">Completed</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Agreements List */}
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardHeader>
            <CardTitle className="text-white font-['Unbounded']">My Project Agreements</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-10 h-10 border-2 border-[#23F7DD] border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : agreements.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 text-[#475569] mx-auto mb-4" />
                <p className="text-[#94A3B8]">No agreements yet</p>
                <p className="text-sm text-[#475569] mt-2">
                  You'll see agreements here when your team leader assigns you to projects
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {agreements.map((agreement) => {
                  const myAllocation = getMyAllocation(agreement);
                  
                  return (
                    <div
                      key={agreement.agreement_id}
                      className="p-4 bg-white/5 rounded-lg border border-white/10 hover:border-[#23F7DD]/30 transition-colors cursor-pointer"
                      onClick={() => openAgreement(agreement)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-white font-medium">{agreement.project_title}</h3>
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(agreement.status)}`}>
                              {agreement.status.replace(/_/g, ' ').toUpperCase()}
                            </span>
                            {myAllocation && !myAllocation.approved && (
                              <span className="px-2 py-0.5 rounded text-xs font-medium bg-red-500/20 text-red-400 animate-pulse">
                                Action Required
                              </span>
                            )}
                          </div>
                          
                          <div className="flex flex-wrap gap-4 text-sm text-[#94A3B8]">
                            <span className="flex items-center gap-1">
                              <MapPin className="w-4 h-4" />
                              {agreement.location}
                            </span>
                            <span className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              {agreement.deadline || "TBD"}
                            </span>
                          </div>
                        </div>
                        
                        <div className="text-right">
                          {myAllocation && (
                            <div className="mb-2">
                              <p className="text-sm text-[#94A3B8]">Your share:</p>
                              <p className="text-xl font-bold text-[#23F7DD]">
                                {formatCurrency(myAllocation.amount)}
                              </p>
                              <p className="text-sm text-white">
                                ({myAllocation.current_percentage}%)
                              </p>
                            </div>
                          )}
                          <ChevronRight className="w-5 h-5 text-[#475569]" />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Agreement Details Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-['Unbounded']">Agreement Details</DialogTitle>
            </DialogHeader>
            
            {selectedAgreement && (
              <div className="space-y-6">
                {/* Project Info */}
                <div className="p-4 bg-white/5 rounded-lg">
                  <h3 className="text-xl font-bold text-white mb-2">{selectedAgreement.project_title}</h3>
                  <p className="text-[#94A3B8] mb-4">Led by: {selectedAgreement.team_leader_name}</p>
                  
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="flex items-center gap-2">
                      <DollarSign className="w-4 h-4 text-[#23F7DD]" />
                      <span className="text-[#94A3B8]">Total:</span>
                      <span className="text-white font-medium">{formatCurrency(selectedAgreement.total_amount)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-[#23F7DD]" />
                      <span className="text-white">{selectedAgreement.location}</span>
                    </div>
                  </div>
                </div>

                {/* My Allocation */}
                {(() => {
                  const myAllocation = getMyAllocation(selectedAgreement);
                  if (!myAllocation) return null;
                  
                  const maxCede = myAllocation.default_percentage - 5;
                  
                  return (
                    <div className="p-4 bg-[#23F7DD]/10 border border-[#23F7DD]/30 rounded-lg">
                      <h4 className="text-white font-medium mb-4 flex items-center gap-2">
                        <DollarSign className="w-5 h-5 text-[#23F7DD]" />
                        Your Allocation
                      </h4>
                      
                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div>
                          <p className="text-sm text-[#94A3B8]">Default share</p>
                          <p className="text-xl font-bold text-white">{myAllocation.default_percentage}%</p>
                        </div>
                        <div>
                          <p className="text-sm text-[#94A3B8]">Current share</p>
                          <p className="text-xl font-bold text-[#23F7DD]">{myAllocation.current_percentage}%</p>
                          <p className="text-white">{formatCurrency(myAllocation.amount)}</p>
                        </div>
                      </div>

                      {!myAllocation.approved && selectedAgreement.status !== "signed" && (
                        <div className="space-y-4 pt-4 border-t border-white/10">
                          <div className="flex items-center gap-2 mb-2">
                            <Gift className="w-5 h-5 text-purple-400" />
                            <span className="text-white font-medium">Cede percentage to Team Leader</span>
                          </div>
                          <p className="text-sm text-[#94A3B8]">
                            You can optionally give a portion of your share to the team leader for their extra responsibilities.
                          </p>
                          
                          <div className="space-y-2">
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-[#94A3B8]">Cede: {cedePercentage}%</span>
                              <span className="text-[#94A3B8]">Max: {maxCede}%</span>
                            </div>
                            <Slider
                              value={[cedePercentage]}
                              onValueChange={(vals) => setCedePercentage(vals[0])}
                              max={maxCede}
                              min={0}
                              step={1}
                              className="w-full"
                            />
                            <p className="text-sm text-center text-[#94A3B8]">
                              Your new share: <span className="text-[#23F7DD] font-bold">{myAllocation.default_percentage - cedePercentage}%</span>
                              {" = "}
                              <span className="text-white">{formatCurrency(selectedAgreement.total_amount * (myAllocation.default_percentage - cedePercentage) / 100)}</span>
                            </p>
                          </div>
                          
                          {cedePercentage !== (myAllocation.ceded_percentage || 0) && (
                            <Button
                              onClick={() => handleUpdateAllocation(selectedAgreement.agreement_id)}
                              disabled={updating}
                              variant="outline"
                              className="w-full border-purple-500/30 text-purple-400 hover:bg-purple-500/10"
                            >
                              {updating ? "Updating..." : "Update Allocation"}
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* All Members */}
                <div className="p-4 bg-white/5 rounded-lg">
                  <h4 className="text-white font-medium mb-4">Team Distribution</h4>
                  <div className="space-y-2">
                    {selectedAgreement.members?.map((member) => (
                      <div 
                        key={member.user_id}
                        className={`flex items-center justify-between p-2 rounded ${
                          member.user_id === user?.user_id ? 'bg-[#23F7DD]/10' : 'bg-black/30'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-0.5 rounded text-xs ${
                            member.role === 'team_leader' ? 'bg-purple-500/20 text-purple-400' : 'bg-gray-500/20 text-gray-400'
                          }`}>
                            {member.role === 'team_leader' ? 'TL' : 'W'}
                          </span>
                          <span className="text-white">{member.user_name}</span>
                          {member.user_id === user?.user_id && (
                            <span className="text-[#23F7DD] text-xs">(You)</span>
                          )}
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-white font-medium">{member.current_percentage}%</span>
                          {member.approved && <CheckCircle2 className="w-4 h-4 text-green-400" />}
                          {member.signed && <Signature className="w-4 h-4 text-blue-400" />}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Actions */}
                {(() => {
                  const myAllocation = getMyAllocation(selectedAgreement);
                  if (!myAllocation) return null;
                  
                  return (
                    <div className="p-4 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                      {!myAllocation.approved ? (
                        <div className="space-y-4">
                          <p className="text-[#94A3B8] text-sm">
                            Review your allocation above. Once satisfied, approve to proceed.
                          </p>
                          <Button
                            onClick={() => handleApprove(selectedAgreement.agreement_id)}
                            className="w-full bg-green-500 text-white hover:bg-green-600"
                          >
                            <CheckCircle2 className="w-4 h-4 mr-2" />
                            Approve My Allocation ({myAllocation.current_percentage}% = {formatCurrency(myAllocation.amount)})
                          </Button>
                        </div>
                      ) : !myAllocation.signed && selectedAgreement.all_members_approved ? (
                        <div className="space-y-4">
                          <p className="text-green-400 text-sm flex items-center gap-2">
                            <CheckCircle2 className="w-4 h-4" />
                            All members approved! Sign to finalize.
                          </p>
                          <div className="space-y-2">
                            <Label className="text-[#94A3B8]">Type your full name to sign</Label>
                            <Input
                              value={signature}
                              onChange={(e) => setSignature(e.target.value)}
                              placeholder="Your full name"
                              className="bg-black/50 border-white/10 text-white focus:border-[#23F7DD]"
                            />
                          </div>
                          <Button
                            onClick={() => handleSign(selectedAgreement.agreement_id)}
                            disabled={signing || !signature.trim()}
                            className="w-full bg-blue-500 text-white hover:bg-blue-600"
                          >
                            {signing ? "Signing..." : (
                              <>
                                <Signature className="w-4 h-4 mr-2" />
                                Sign Agreement
                              </>
                            )}
                          </Button>
                        </div>
                      ) : myAllocation.signed ? (
                        <div className="text-center text-green-400">
                          <CheckCircle2 className="w-8 h-8 mx-auto mb-2" />
                          <p className="font-medium">Agreement Signed!</p>
                          <p className="text-sm text-[#94A3B8]">Signed: {new Date(myAllocation.signed_at).toLocaleString()}</p>
                        </div>
                      ) : (
                        <div className="text-center text-yellow-400">
                          <Clock className="w-8 h-8 mx-auto mb-2" />
                          <p>Waiting for all members to approve...</p>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
